import React, { useEffect, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { Button, Input, Modal, Select, message, Slider } from 'antd'
import { FormatPainterOutlined, UndoOutlined } from '@ant-design/icons'
import { ColorPicker } from './color_picker'
import { getCdnUrl } from '../consts'

const fn = () => {}
const STORAGE_KEY = 'generate-nl-image-state'
const trianglePositions = [
  'top left',
  'top right',
  'bottom right',
  'bottom left',
]

const COLORS = [
  ['#b50018', 'Gambero'],
  ['#663d5e', 'Jancis Robinson'],
  ['#30161f', 'Parker'],
  ['#004a39', 'Penin'],
  ['#4d4d4e', 'Suckling'],
  ['#ba3c40', 'Vinous'],
  ['#aa3130', 'Viti'],
  ['#930223', 'Wine Spectator'],
  ['#460418', 'Decanter'],
  ['#9d0a41', 'Tim Atkins'],
  ['#FDD203', 'Offers'],
]

export function GenerateProductImageWithTriangle({
  productRef,
  defaultBgColor,
  onUpload = fn,
  onChangeBgColor = fn,
  ...props
}) {
  const container = useRef()
  const [uploading, setUploading] = useState(false)
  const [imageSrc, setImageSrc] = useState()
  const [modal, setModal] = useState()

  const initialState = {
    bgColor: defaultBgColor || '#e9edeb',
    triangleColor: '#fbe89d',
    width: 400,
    height: 352,
    trianglePosition: trianglePositions[0],
    ballRadius: 70,
    ballColor: '#000000',
    ballBorderWidth: 1,
    ballContentTxt: '10%',
    ballContentFooter: '',
    ballFontSize: 40,
    ballFooterFontsize: 40,
    zoomImg: 1,
    zoomImgPosition: 10,
  }

  const [state, setState] = useState(() => {
    const state = localStorage.getItem(STORAGE_KEY)
    return state ? JSON.parse(state) : initialState
  })

  const {
    width,
    height,
    bgColor,
    triangleColor,
    trianglePosition,
    ballRadius,
    ballBorderWidth,
    ballColor,
    ballContentTxt,
    ballFooterTxt,
    ballFontSize,
    ballFooterFontsize,
    zoomImg,
    zoomImgPosition,
  } = state

  const style = {
    width,
    height,
    overflow: 'hidden',
    position: 'relative',
    margin: '20px auto',
    background: `linear-gradient(to ${trianglePosition}, ${bgColor} 0%, ${bgColor} 50%, ${triangleColor} 50%, ${triangleColor} 100%)`,
  }

  const ballStyle = {
    border: `${ballBorderWidth}px solid ${ballColor}`,
    color: ballColor,
    borderRadius: '50%',
    width: ballRadius * 2,
    height: ballRadius * 2,
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: ballFontSize,
    ...trianglePosition
      .split(' ')
      .reduce((o, pos) => ({ ...o, [pos]: width * 0.05 }), {}),
  }

  const { zoomWidth, zoomHeight, zoomTop } = getZoomProps(
    zoomImg,
    zoomImgPosition
  )
  const imgStyle = {
    height: zoomHeight,
    objectFit: 'cover',
    width: zoomWidth,
    top: zoomTop,
    position: 'absolute',
    ...(trianglePosition.includes('right') ? { left: 45 } : { right: 45 }),
  }

  const footerTextStyle = {
    position: 'absolute',
    fontSize: ballFooterFontsize,
    bottom: -25 - ballFooterFontsize - ballBorderWidth,
  }

  async function onOpenModal() {
    if (!productRef) return message.warning('No product selected')
    setModal(true)
    if (!imageSrc) setImageSrc(await loadProductImage(productRef))
  }

  async function upload() {
    setUploading(true)
    const canvas = await html2canvas(container.current, {
      allowTaint: true,
      useCORS: true,
    })
    const blob = await new Promise((res) => canvas.toBlob(res))
    onChangeBgColor(bgColor)
    onUpload(
      {
        contentType: 'image/png',
        data: await blob.arrayBuffer(),
        filename: `generated_image_${Date.now()}.png`,
      },
      (ok) => {
        setUploading(false)
        if (ok) {
          message.success('Image uploaded')
          setModal(false)
        }
      }
    )
  }

  useEffect(saveToStorage, [state])
  function saveToStorage() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }

  return (
    <>
      <Button type="button" onClick={onOpenModal} {...props}>
        <FormatPainterOutlined /> Generate image
      </Button>
      <Modal
        title={
          <>
            <FormatPainterOutlined /> Generate image
          </>
        }
        width={1000}
        visible={modal}
        okText={uploading ? 'Uploading...' : 'Upload generated image'}
        onOk={upload}
        onCancel={() => setModal(false)}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            type="button"
            onClick={() => {
              setState(initialState)
            }}
          >
            <UndoOutlined /> Reset to initial state
          </Button>
        </div>
        <div style={{ display: 'flex', fontFamily: 'Roboto' }}>
          <form style={{ width: 500 }}>
            <label className="label">Product image zoom</label>
            <Slider
              min={1}
              max={15}
              onChange={(v) => setState({ ...state, zoomImg: v })}
              value={zoomImg}
            />
            <label className="label">Product image position</label>

            <Slider
              min={1}
              marks={{ 1: 'Display top', 10: 'center', 20: 'Display bottom' }}
              max={20}
              onChange={(v) => setState({ ...state, zoomImgPosition: v })}
              value={zoomImgPosition}
            />
            <ColorPicker
              label="Product background color"
              key="generate-image-bg-color"
              extraColors={COLORS}
              onChange={(e) => setState({ ...state, bgColor: e.target.value })}
              value={bgColor}
            />
            <ColorPicker
              label="Info background color"
              key="generate-image-triangle-color"
              extraColors={COLORS}
              onChange={(e) =>
                setState({ ...state, triangleColor: e.target.value })
              }
              value={triangleColor}
            />
            <ColorPicker
              label="Info color"
              extraColors={COLORS}
              key="generate-image-ball-border-color"
              onChange={(e) =>
                setState({ ...state, ballColor: e.target.value })
              }
              value={ballColor}
            />
            <label className="label">Info position</label>
            <Select
              style={{ width: '100%' }}
              value={trianglePosition}
              onChange={(v) => setState({ ...state, trianglePosition: v })}
            >
              {trianglePositions.map((position) => (
                <Select.Option key={position} value={position}>
                  {position}
                </Select.Option>
              ))}
            </Select>
            <label className="label">Ball content text</label>
            <Input
              value={ballContentTxt}
              onChange={(e) =>
                setState({ ...state, ballContentTxt: e.target.value })
              }
            />
            <label className="label">Ball font size</label>
            <Input
              type="number"
              value={ballFontSize}
              onChange={(e) =>
                setState({ ...state, ballFontSize: e.target.valueAsNumber })
              }
            />
            <label className="label">Ball footer text</label>
            <Input
              value={ballFooterTxt}
              onChange={(e) =>
                setState({ ...state, ballFooterTxt: e.target.value })
              }
            />
            <label className="label">Ball footer font size</label>
            <Input
              type="number"
              value={ballFooterFontsize}
              onChange={(e) =>
                setState({
                  ...state,
                  ballFooterFontsize: e.target.valueAsNumber,
                })
              }
            />
            <label className="label">Ball radius</label>
            <Input
              type="number"
              value={ballRadius}
              onChange={(e) =>
                setState({ ...state, ballRadius: e.target.valueAsNumber })
              }
            />

            <label className="label">Ball border width</label>
            <Input
              type="number"
              value={ballBorderWidth}
              onChange={(e) =>
                setState({ ...state, ballBorderWidth: e.target.valueAsNumber })
              }
            />
          </form>

          <div style={style} ref={container}>
            <div style={ballStyle}>
              {ballContentTxt}
              {ballFooterTxt && (
                <div style={footerTextStyle}>{ballFooterTxt}</div>
              )}
            </div>
            {imageSrc && (
              <img
                crossorigin="anonymous"
                style={imgStyle}
                src={imageSrc}
                alt="product"
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

async function loadProductImage(ref) {
  const res = await fetch(
    `/db/guillotina/@product?id=${ref}&lang=es&bodega_extras=1`
  ).then((r) => r.json())
  const path =
    res.product.images?.[`${ref}_anv800`]?.path ||
    `/prfmtgrande/vi/${ref}_anv800.png`
  return getCdnUrl() + path
}

function getZoomProps(zoomImg, zoomImgPosition) {
  const defaultWidth = 24
  const zoomWidth = defaultWidth + zoomImg + '%'
  const zoomHeight = (zoomWidth * 100) / defaultWidth + '%'
  const zoomTop = -zoomImg * zoomImgPosition

  return { zoomWidth, zoomHeight, zoomTop }
}
